import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, IconButton, Tooltip } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { Delete } from "@mui/icons-material";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/plugins/counter.css";
import { openInNewTab } from "../../utils/openInNewTab";


const FileUpload = ({ onFileChange, clearTrigger }) => {
  const [files, setFiles] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleImageClick = (clickedIndex) => {
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    const images = imageFiles.map((file) => ({
      src: URL.createObjectURL(file),
    }));

    const clickedFile = files[clickedIndex];
    const actualIndex = imageFiles.indexOf(clickedFile);

    setLightboxImages(images);
    setCurrentImageIndex(actualIndex >= 0 ? actualIndex : 0);
    setLightboxOpen(true);
  };

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const newFiles = [...files, ...acceptedFiles];
      setFiles(newFiles);
      onFileChange(newFiles); // Send array of files to the parent
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    // accept: [
    //   "image/*",
    //   "application/pdf",
    //   "application/msword",
    //   "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    //   "application/vnd.ms-excel",
    //   "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    //   "text/csv",
    // ],
        accept: {
      "image/*": [],
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
      "application/vnd.ms-excel": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
      "text/csv": [],
    },
    maxFiles: Infinity, // Allow multiple files
  });

  const handleRemove = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    onFileChange(newFiles);
  };

  useEffect(() => {
    if (clearTrigger) {
      setFiles([]);
      onFileChange([]);
    }
  }, [clearTrigger, onFileChange]);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" width="100%">
      <Box
        {...getRootProps()}
        sx={{
          borderRadius: 2,
          padding: 2,
          textAlign: "center",
          cursor: "pointer",
          border: "2px dashed lightgray",
          width: "100%",
          transition: "border-color 0.3s ease",
          "&:hover": {
            borderColor: "black",
          },
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="body1" className="text-rgba-gray">
          Upload Files | Drag and Drop
        </Typography>
      </Box>

      {files.length > 0 && (
        <Box mt={2}>
          {files.map((file, index) => (
            <Paper
              key={index}
              elevation={3}
              sx={{
                marginBottom: 1,
                padding: 1,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              {file.type.startsWith("image/") && (
                <img
                  src={URL.createObjectURL(file)}
                  alt="file thumbnail"
                  style={{ width: 50, height: 50, objectFit: "cover", cursor: "pointer" }}
                  onClick={() => handleImageClick(index)}
                />
              )}
              <Box>
                <Typography variant="body1">{file.name}</Typography>
              </Box>
              <Tooltip title="Remove File">
                <IconButton onClick={() => handleRemove(index)}>
                  <Delete className="text-rgbr-red" />
                </IconButton>
              </Tooltip>
            </Paper>
          ))}
        </Box>
      )}

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={lightboxImages}
        index={currentImageIndex}
        on={{
          view: ({ index }) => setCurrentImageIndex(index),
        }}
        plugins={lightboxImages.length > 1 ? [Zoom, Counter] : [Zoom]}
        render={{
          buttonPrev: lightboxImages.length <= 1 ? () => null : undefined,
          buttonNext: lightboxImages.length <= 1 ? () => null : undefined,
        }}
        zoom={{
          maxZoomPixelRatio: 5,
          scrollToZoom: true,
        }}
      />
    </Box>
  );
};

export default FileUpload;



export const SingleFileUpload = ({ onFileChange, clearTrigger }) => {
  const [file, setFile] = useState(null);
  const [singleLightboxOpen, setSingleLightboxOpen] = useState(false);

  const onDrop = (acceptedFiles) => {
    // Ensure acceptedFiles is an array and has files
    if (Array.isArray(acceptedFiles) && acceptedFiles.length > 0) {
      const uploadedFile = acceptedFiles[0];
      setFile(uploadedFile);
      onFileChange(uploadedFile); // Pass the file to the parent
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    // accept: ['image/*', '.pdf', '.docx'],
    // accept: [
    //   "image/*",
    //   "application/pdf", // PDF
    //   "application/msword", // DOC
    //   "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX
    //   "application/vnd.ms-excel", // XLS
    //   "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
    //   "text/csv",
    // ],
      accept: {
      "image/*": [],
      "application/pdf": [], // PDF
      "application/msword": [], // DOC
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [], // DOCX
      "application/vnd.ms-excel": [], // XLS
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [], // XLSX
      "text/csv": [],
    },
    maxFiles: 1,
  });

  const handleRemove = () => {
    setFile(null);
    onFileChange(null); // Clear the file in the parent as well
  };

  useEffect(() => {
    if (clearTrigger) {
      setFile(null);
      onFileChange(null);
    }
  }, [clearTrigger, onFileChange]);

  const fileExtension = file ? file.name.split(".").pop() : "";
  const fileName = file ? file.name : "";
  const fileImageUrl =
    file && file.type.startsWith("image/") ? URL.createObjectURL(file) : "";
  useEffect(() => {
    if (!file) {
      setFile(null);
    }
  }, [file]);
  
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
    >
      <Box
        {...getRootProps()}
        sx={{
          borderRadius: 2,
          padding: 2,
          textAlign: "center",
          cursor: "pointer",
          border: "2px dashed lightgray",
          width: "100%",
          transition: "border-color 0.3s ease",
          "&:hover": {
            borderColor: "black",
          },
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="body1" className="text-rgba-gray">
          Upload File | Drag and Drop
        </Typography>
      </Box>

      {file && (
        <Paper
          elevation={3}
          sx={{
            marginBottom: 1,
            padding: 1,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {fileImageUrl ? (
            <img
              src={fileImageUrl}
              alt="file thumbnail"
              style={{ width: 50, height: 50, objectFit: "cover", cursor: "pointer" }}
              onClick={() => setSingleLightboxOpen(true)}
            />
          ) : (
            <Typography></Typography>
          )}
          <Box>
            <Typography variant="body1">{fileName}</Typography>
            {/* <Typography variant="body2" color="textSecondary">
              {fileExtension?.toUpperCase()}
            </Typography> */}
          </Box>
          <Tooltip title="Remove File">
            <IconButton onClick={handleRemove}>
              <Delete className="text-rgbr-red" />
            </IconButton>
          </Tooltip>
        </Paper>
      )}
      {fileImageUrl && (
        <Lightbox
          open={singleLightboxOpen}
          close={() => setSingleLightboxOpen(false)}
          slides={[{ src: fileImageUrl }]}
          plugins={[Zoom]}
          render={{
            buttonPrev: () => null,
            buttonNext: () => null,
          }}
          zoom={{
            maxZoomPixelRatio: 5,
            scrollToZoom: true,
          }}
        />
      )}
    </Box>
  );
};

export const ApprisalFileUpload = ({
  onFileChange,
  clearTrigger,
  multiple = true,
  accept,
  initialFiles = [],
  accept_type,
}) => {
  const [files, setFiles] = useState([]);

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const newFiles = [...files, ...acceptedFiles];
      setFiles(newFiles);
      onFileChange(newFiles);
    }
  };
  const getAcceptObject = () => {
    if (accept_type) {
      // If accept_type is already an object, use it
      if (typeof accept_type === "object") {
        return accept_type;
      }

      // If it's a string, convert it to object format
      const mimeTypes = accept_type.split(",").map((type) => type.trim());
      const acceptObject = {};
      mimeTypes.forEach((mimeType) => {
        acceptObject[mimeType] = [];
      });
      return acceptObject;
    }

    // Default accept types
    return {
      "image/*": [],
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
      "application/vnd.ms-excel": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
      "text/csv": [],
    };
  };
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: getAcceptObject(),
    maxFiles: multiple ? Infinity : 1,
  });
  const handleRemove = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    onFileChange(newFiles);
  };

  const handleDownload = (file, index) => {
    const isExistingFile = file.url && !file.type;

    if (isExistingFile) {
      const link = document.createElement("a");
      link.href = file.url;
      link.download = file.name || `file-${index}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    if (clearTrigger) {
      setFiles([]);
      onFileChange([]);
    }
  }, [clearTrigger, onFileChange]);

  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      setFiles([...initialFiles]);
      onFileChange([...initialFiles]);
    } else {
      setFiles([]);
    }
  }, [initialFiles]);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" width="100%">
      <Box
        {...getRootProps()}
        sx={{
          borderRadius: 2,
          padding: 2,
          textAlign: "center",
          cursor: "pointer",
          border: "2px dashed lightgray",
          width: "100%",
          transition: "border-color 0.3s ease",
          "&:hover": {
            borderColor: "black",
          },
        }}
      >
        <input
          {...getInputProps()}
          // type="file"
          // multiple={multiple}
          // accept="
          // accept={accept_type ? accept_type : "image/jpeg,image/png,image/jpg,image/gif,image/svg+xml"}

          // onChange={handleChange}
          // style={{ display: 'none' }}
          // ref={fileInputRef}
        />
        <Typography variant="body1" className="text-rgba-gray">
          Upload Files | Drag and Drop
        </Typography>
      </Box>

      {files.length > 0 && (
        <Box mt={2}>
          {files.map((file, index) => {
            const isExistingFile = file.url && !file.type;
            const imageUrl = isExistingFile
              ? file.url
              : file.type?.startsWith("image/")
                ? URL.createObjectURL(file)
                : null;

            return (
              <Paper
                key={index}
                elevation={3}
                sx={{
                  marginBottom: 1,
                  padding: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {imageUrl && (
                  <img
                    src={imageUrl}
                    alt="file thumbnail"
                    style={{
                      width: 50,
                      height: 50,
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                    onClick={() => openInNewTab(imageUrl)}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
                <Box flex={1}>
                  <Typography variant="body1">{file.name}</Typography>
                  {isExistingFile && (
                    <Typography variant="caption" color="textSecondary">
                      Existing file
                    </Typography>
                  )}
                </Box>
                <Tooltip title="Remove File">
                  <IconButton onClick={() => handleRemove(index)}>
                    <Delete className="text-rgbr-red" />
                  </IconButton>
                </Tooltip>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
